import { clerkApiInterceptor } from '../authenticatedApi/clerkApiInterceptor';
import globals from '../../common/globals';

const API_NAME = globals.PLATFORM_API_NAME;

// Forces to return the entire Axios response object instead of only response.data
const returnAxiosResponse = { response: true };

// Type definitions
type DeploymentStatus = 'active' | 'inactive';
type RuntimeStatus = 'healthy' | 'degraded' | 'unhealthy';


/**
 * Artifact-based recipe source
 */
export type ArtifactRecipeSource = {
  type: 'artifact';
  recipeUuid: string;
  artifactUrl: string;
	name: string;
  version: number;
	recipeDbId?: string;
	/** the recipe contents to be deployed */
	recipeBlob: string;
};


/**
 * Git-based recipe source
 */
export type GitRecipeSource = {
  type: 'git';
  repositoryUrl: string;
  ref: string;
  subPath?: string;
	name: string;
};

/**
 * Recipe source union type
 */
export type RecipeSource = ArtifactRecipeSource | GitRecipeSource;

export type CreateDeploymentRequest = {
	environmentId: string;
	recipeSource: RecipeSource;
	config?: Record<string, any>;
	desiredWorkerCount?: number;
	encryptedSecretsBlob?: string;
	secretsFormatVersion?: string;
	notes?: string;
};

type Deployment = {
	id: string;
	organizationId: string;
	environmentId: string;
	recipeSource: RecipeSource;
	config: Record<string, any>;
	desiredWorkerCount: number;
	status: 'active' | 'inactive' | 'failed' | 'superseded';
	createdAt: string;
	updatedAt: string;
	encryptedSecretsBlob?: string;
	secretsFormatVersion?: string;
	notes?: string;
};

type UpdateDeploymentRequest = {
	replicas?: number;
	status?: DeploymentStatus;
	config?: Record<string, any>;
};

type DeploymentRuntimeStatus = {
	deploymentId: string;
	status: RuntimeStatus;
	replicas: {
		desired: number;
		running: number;
		starting: number;
		failed: number;
	};
	workers: Array<{
		hostId: string;
		status: string;
	}>;
};

export type KillWorkerCommandPayload = {
  workerId: string;
	hostId: string;
};

export type RestartDeploymentRequest = {
	deploymentId: string;
	hostId?: string;
};

export type CreateEnvironmentRequest = {
	name: string;
  description?: string;
  tags?: Record<string, string>;
	private?: boolean;
};

export type Environment = {
	id: string;
	organizationId: string;
	name: string;
	description?: string;
	tags?: Record<string, string>;
	createdAt: string;
	updatedAt: string;
	private?: boolean;
	createdBy?: string;
};


/**
 * Environment with runtime metrics including hosts, active deployments, and active processes
 */
export type EnvironmentWithMetrics = Environment & {
	/** List of hosts in this environment */
	hosts: Host[];
	/** List of active deployments in this environment */
	activeDeployments: Array<{
		id: string;
		environmentId: string;
		recipeSource: RecipeSource;
		createdAt: string;
		updatedAt: string;
	}>;
	/** Number of active processes across all hosts in this environment */
	activeProcesses: number;
};

export type ListEnvironmentsResponse = EnvironmentWithMetrics[];


export type Host = {
	id: string;
	organizationId: string;
	environmentId: string;
	name: string;
	hostname?: string;
	ipAddress?: string;
	createdAt: string;
	updatedAt: string;
};

export type ListHostsResponse = Host[];

export type HostMetrics = {
	cpuPercent?: number;
  memoryUsedBytes?: number;
  memoryTotalBytes?: number;
  loadAverage?: number[];
  uptimeSeconds?: number;
};

/**
 * Worker state values
 */
export type WorkerState = 'starting' | 'running' | 'failed' | 'stopped';

/**
 * Worker metrics included in worker reports
 */
export type WorkerMetrics = {
  cpuPercent?: number;
  memoryUsedBytes?: number;
  uptimeSeconds?: number;
};

export type WorkerReport = {
  pid: number;
  workerId: string;
  deploymentId: string;
  state: WorkerState;
  startedAt: string;
  stoppedAt?: string;
  exitCode?: number;
  errorMessage?: string;
  resourceUsage?: {
    cpu: number;
    memory: number;
  };
  metrics?: WorkerMetrics;
	lastLogLines?: string[];
};

export type DeploymentRuntimeInfo = Deployment & {
	workers: WorkerReport[];
};

export type GetHostResponse = {
	host: Host & {
		/** a list of deployment IDs that have failed on this host */
		failedDeployments?: string[];
	};
	latestHeartbeat?: HostMetrics; 
	deployments: DeploymentRuntimeInfo[];
};

/**
 * Extended deployment info with environment and host context
 */
export type DeploymentWithContext = DeploymentRuntimeInfo & {
	environmentId: string;
	environmentName: string;
	hostId: string;
	hostName: string;
	hostIpAddress?: string;
};

/**
 * Extended worker report with deployment and environment context
 */
export type WorkerReportWithContext = WorkerReport & {
	deploymentId: string;
	recipeName: string;
	recipeVersion?: number;
	environmentId: string;
	environmentName: string;
	hostId: string;
	hostName: string;
	hostIpAddress?: string;
};

/**
 * Overview metrics for the orchestration dashboard
 */
export type OverviewMetrics = {
	environments: number;
	hosts: number;
	deployments: number;
	processes: number;
	failedProcesses: number;
};


/**
 * Updates an existing deployment (scaling, config, status).
 * @param config the deployment ID and update information
 */
const updateDeployment = async (config: {
	deploymentId: string;
	deploymentInfo: UpdateDeploymentRequest;
}): Promise<Deployment> => {
	// PATCH /v1/deployments/:deploymentId
	const response = await clerkApiInterceptor.patch(API_NAME, `/v1/deployments/${config.deploymentId}`, { ...returnAxiosResponse, body: config.deploymentInfo });
	const data = response.data as Deployment;
	return data;
};

/**
 * Retrieves details of a single deployment.
 * @param deploymentId the ID of the deployment to retrieve
 */
const getDeployment = async (deploymentId: string): Promise<Deployment> => {
	// GET /v1/deployments/:deploymentId
	const response = await clerkApiInterceptor.get(API_NAME, `/v1/deployments/${deploymentId}`, returnAxiosResponse);
	const data = response.data as Deployment;
	return data;
};

/**
 * Gets the runtime health and worker status of a deployment.
 * @param deploymentId the ID of the deployment to get status for
 */
const getDeploymentStatus = async (deploymentId: string): Promise<DeploymentRuntimeStatus> => {
	// GET /v1/deployments/:deploymentId/status
	const response = await clerkApiInterceptor.get(API_NAME, `/v1/deployments/${deploymentId}/status`, returnAxiosResponse);
	const data = response.data as DeploymentRuntimeStatus;
	return data;
};

/**
 * Creates a logical environment (e.g., "production", "staging").
 * @param environmentInfo the environment information to create
 */
const createEnvironment = async (environmentInfo: CreateEnvironmentRequest): Promise<Environment> => {
	// POST /v1/environments
	const response = await clerkApiInterceptor.post(API_NAME, `/v1/environments`, { ...returnAxiosResponse, body: environmentInfo });
	const data = response.data as Environment;
	return data;
};

/**
 * Lists all environments for the authenticated organization.
 */
const listEnvironments = async (): Promise<ListEnvironmentsResponse> => {
	// GET /v1/environments
	const response = await clerkApiInterceptor.get(API_NAME, `/v1/environments`, returnAxiosResponse);
	const data = response.data as ListEnvironmentsResponse;
	return data;
};

/**
 * Gets details of a specific environment.
 * @param environmentId the ID of the environment to retrieve
 */
const getEnvironment = async (environmentId: string): Promise<Environment> => {
	// GET /v1/environments/:environmentId
	const response = await clerkApiInterceptor.get(API_NAME, `/v1/environments/${environmentId}`, returnAxiosResponse);
	const data = response.data as Environment;
	return data;
};

/**
 * Updates an existing environment.
 * @param environmentId the ID of the environment to retrieve
 * @returns the updated environment
 */
const updateEnvironment = async (environmentId: string, environmentInfo: Partial<CreateEnvironmentRequest>): Promise<Environment> => {
	// PATCH /v1/environments/:environmentId
	const response = await clerkApiInterceptor.patch(API_NAME, `/v1/environments/${environmentId}`, { ...returnAxiosResponse, body: environmentInfo });
	const data = response.data as Environment;
	return data;
};

/**
 * Deletes an environment.
 * @param environmentId the ID of the environment to delete
 */
const deleteEnvironment = async (environmentId: string): Promise<void> => {
	// DELETE /v1/environments/:environmentId
	await clerkApiInterceptor.delete(API_NAME, `/v1/environments/${environmentId}`, returnAxiosResponse);
};

// /**
//  * Lists all deployments belonging to a specific environment.
//  * @param environmentId the ID of the environment to list deployments for
//  */
// const listDeploymentsInEnvironment = async (environmentId: string): Promise<Deployment[]> => {
// 	// GET /v1/environments/:environmentId/deployments
// 	const response = await clerkApiInterceptor.get(API_NAME, `/v1/environments/${environmentId}/deployments`, returnAxiosResponse);
// 	const data = response.data as Deployment[];
// 	return data;
// };

/**
 * Lists all hosts in a specific environment.
 * @param environmentId the ID of the environment to list hosts for
 */
const listHosts = async (environmentId: string): Promise<ListHostsResponse> => {
	// GET /v1/environments/:environmentId/hosts
	const response = await clerkApiInterceptor.get(API_NAME, `/v1/environments/${environmentId}/hosts`, returnAxiosResponse);
	const data = response.data as ListHostsResponse;
	return data;
};

/**
 * Gets details of a specific host including its deployments.
 * @param config the environment ID and host ID
 */
const getHost = async (config: {
	environmentId: string;
	hostId: string;
}): Promise<GetHostResponse> => {
	// GET /v1/environments/:environmentId/hosts/:hostId
	const response = await clerkApiInterceptor.get(API_NAME, `/v1/environments/${config.environmentId}/hosts/${config.hostId}`, returnAxiosResponse);
	const data = response.data as GetHostResponse;
	return data;
};

/**
 * Restarts (kills) a worker process.
 * @param config the deployment ID, host ID and PID of the worker to restart
 */
const restartWorker = async (config: {
	deploymentId: string;
	workerId: string;
	hostId: string;
}): Promise<{ success: boolean; message: string; commandId: string }> => {
	// POST /v1/deployments/:deploymentId/workers/restart
	const body: KillWorkerCommandPayload = {
		hostId: config.hostId,
		workerId: config.workerId,
	}

	const response = await clerkApiInterceptor.post(
		API_NAME, 
		`/v1/deployments/${config.deploymentId}/workers/restart`, 
		{ 
			...returnAxiosResponse, 
			body, 
		}
	);
	return response.data as { success: boolean; message: string; commandId: string };
};

/**
 * Clears the failed deployment state to allow the deployment to be retried.
 * If hostId is provided, clears for that specific host only.
 * Otherwise, clears for all hosts in the environment.
 * @param config the deployment ID and optional host ID
 */
const restartDeployment = async (config: RestartDeploymentRequest): Promise<{ success: boolean; message: string }> => {
	// POST /v1/deployments/:deploymentId/restart
	const response = await clerkApiInterceptor.post(
		API_NAME,
		`/v1/deployments/${config.deploymentId}/restart`,
		{
			...returnAxiosResponse,
			...(config.hostId && { params: { hostId: config.hostId } }),
		}
	);
	return response.data as { success: boolean; message: string };
};

/**
 * Lists all deployments across all environments for the authenticated organization.
 * @returns Array of deployments with enriched environment and host information
 */
const listAllDeployments = async (): Promise<DeploymentWithContext[]> => {
	// GET /v1/deployments
	const response = await clerkApiInterceptor.get(API_NAME, `/v1/control-plane/deployments`, returnAxiosResponse);
	const data = response.data as DeploymentWithContext[];
	return data;
};

/**
 * Lists all workers/processes across all deployments for the authenticated organization.
 * @returns Array of worker reports enriched with deployment, environment, and host metadata
 */
const listAllProcesses = async (): Promise<WorkerReportWithContext[]> => {
	// GET /v1/control-plane/processes
	const response = await clerkApiInterceptor.get(API_NAME, `/v1/control-plane/processes`, returnAxiosResponse);
	const data = response.data as WorkerReportWithContext[];
	return data;
};

/**
 * Gets overview metrics for the orchestration dashboard.
 * @returns Aggregate metrics across all environments
 */
const getOverviewMetrics = async (): Promise<OverviewMetrics> => {
	// GET /v1/control-plane/orchestration/overview
	const response = await clerkApiInterceptor.get(API_NAME, `/v1/control-plane/orchestration/overview`, returnAxiosResponse);
	const data = response.data as OverviewMetrics;
	return data;
};

/**
 * Gets a single worker entity with enriched metadata including logs.
 * @param workerId the ID of the worker to retrieve
 * @returns Worker report enriched with deployment, environment, and host information
 */
const getWorker = async (workerId: string): Promise<WorkerReportWithContext> => {
	// GET /v1/control-plane/workers/:workerId
	const response = await clerkApiInterceptor.get(API_NAME, `/v1/control-plane/workers/${workerId}`, returnAxiosResponse);
	const data = response.data as WorkerReportWithContext;
	return data;
};


export default {
	updateDeployment,
	getDeployment,
	getDeploymentStatus,
	createEnvironment,
	listEnvironments,
	getEnvironment,
	listHosts,
	getHost,
	updateEnvironment,
	deleteEnvironment,
	restartWorker,
	restartDeployment,
	listAllDeployments,
	listAllProcesses,
	getOverviewMetrics,
	getWorker,
};

